import React, { useEffect, useState, useCallback } from "react";
import AppNav from "@/components/site/AppNav";
import { api } from "@/lib/api";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Shield, ShieldOff, Trash2, Ban, CheckCircle2, Coins, Edit3, EyeOff, Eye, Save, X as XIcon } from "lucide-react";

const PLAN_OPTIONS = ["free", "starter", "pro", "studio"];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [gens, setGens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: "", slug: "", excerpt: "", body: "", tags: "" });

  // Content editor state
  const [editingGenId, setEditingGenId] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState("");
  const [editingOutput, setEditingOutput] = useState("");

  async function openGenEditor(g) {
    setEditingGenId(g.id);
    setEditingPrompt(g.prompt || "");
    // Fetch full detail (list endpoint only ships a preview)
    try {
      const r = await api.get(`/admin/generations/${g.id}`);
      setEditingOutput(r.data.output || r.data.preview || "");
    } catch {
      setEditingOutput(g.preview || "");
    }
  }

  async function saveGenEdit(g) {
    try {
      await api.patch(`/admin/generations/${g.id}`, {
        prompt: editingPrompt,
        output: g.kind === "text" ? editingOutput : undefined,
      });
      toast.success("Saved");
      setEditingGenId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  }

  async function toggleGenHidden(g) {
    try {
      await api.patch(`/admin/generations/${g.id}`, { hidden: !g.hidden });
      toast.success(g.hidden ? "Unhidden" : "Hidden from user");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }

  async function deleteGen(g) {
    if (!window.confirm(`Delete this ${g.kind} generation permanently?`)) return;
    try {
      await api.delete(`/admin/generations/${g.id}`);
      toast.success("Generation deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }

  const load = useCallback(async () => {
    try {
      const [s, u, g] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/generations"),
      ]);
      setStats(s.data);
      setUsers(u.data.users);
      setGens(g.data.generations);
    } catch {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function changePlan(id, plan_code) {
    setBusyId(id);
    try {
      await api.post(`/admin/users/${id}/plan`, { plan_code, status: plan_code === "free" ? "canceled" : "active" });
      toast.success(`Plan set to ${plan_code}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  }

  async function toggleRole(u) {
    setBusyId(u.id);
    try {
      const nextRole = u.role === "admin" ? "user" : "admin";
      await api.post(`/admin/users/${u.id}/role`, { role: nextRole });
      toast.success(`${u.email} is now ${nextRole}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  }

  async function toggleBan(u) {
    setBusyId(u.id);
    try {
      const endpoint = u.is_banned ? "unban" : "ban";
      await api.post(`/admin/users/${u.id}/${endpoint}`);
      toast.success(u.is_banned ? "User unbanned" : "User banned");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  }

  async function deleteUser(u) {
    if (!window.confirm(`Permanently delete ${u.email}? This removes all their generations and subscriptions.`)) return;
    setBusyId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  }

  async function adjustCredits(u) {
    const raw = window.prompt(
      `Adjust credits for ${u.email}. Enter a positive number to add, negative to deduct.\nCurrent balance: ${u.credits ?? 0}`,
      "10"
    );
    if (!raw) return;
    const delta = parseInt(raw, 10);
    if (!Number.isFinite(delta) || delta === 0) {
      toast.error("Enter a non-zero whole number.");
      return;
    }
    setBusyId(u.id);
    try {
      const r = await api.post(`/admin/users/${u.id}/credits`, { delta });
      toast.success(`Credits: ${r.data.credits_before} → ${r.data.credits_after}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  }

  async function submitBlog(e) {
    e.preventDefault();
    try {
      await api.post("/admin/blog", {
        title: blogForm.title,
        slug: blogForm.slug || blogForm.title.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        excerpt: blogForm.excerpt,
        body: blogForm.body,
        tags: blogForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Post published");
      setBlogForm({ title: "", slug: "", excerpt: "", body: "", tags: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publish failed");
    }
  }

  const planData = stats
    ? Object.entries(stats.plan_breakdown || {}).map(([code, count]) => ({ code, count }))
    : [];

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <AppNav />
      <main className="max-w-[1400px] mx-auto">
        <section className="p-8 md:p-12 border-b border-border">
          <div className="overline text-muted-foreground">Admin Console</div>
          <h1 className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mt-4">
            The room where it counts.
          </h1>
          <p className="mt-4 text-muted-foreground max-w-2xl">
            Full control — change plans, promote admins, ban abusers, delete accounts. Every
            action is instantly reflected across the app.
          </p>
        </section>

        {loading || !stats ? (
          <div className="p-12 overline text-muted-foreground">Loading admin data…</div>
        ) : (
          <>
            <section className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-l border-t border-border">
              {[
                { k: stats.revenue_display, v: "Gross revenue" },
                { k: stats.total_users, v: "Users" },
                { k: stats.active_subscriptions, v: "Active subs" },
                { k: stats.total_generations, v: "Total generations" },
              ].map((s, i) => (
                <div key={i} className="p-6 md:p-8 border-r border-b border-border" data-testid={`admin-stat-${i}`}>
                  <div className="font-serif-display text-4xl md:text-5xl leading-none">{s.k}</div>
                  <div className="overline text-muted-foreground mt-2">{s.v}</div>
                </div>
              ))}
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-12">
              <div className="lg:col-span-6 border-r border-b border-border p-8">
                <div className="overline text-muted-foreground">Active subs by plan</div>
                {planData.length ? (
                  <div className="mt-6 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={planData}>
                        <XAxis dataKey="code" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 12, fontFamily: "JetBrains Mono" }} />
                        <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 12 }} />
                        <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ borderRadius: 0 }} />
                        <Bar dataKey="count">
                          {planData.map((_, idx) => (
                            <Cell key={idx} fill="hsl(var(--primary))" />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="mt-6 text-sm text-muted-foreground">No paid subs yet.</div>
                )}
              </div>
              <div className="lg:col-span-6 border-b border-border p-8">
                <div className="overline text-muted-foreground">Content mix</div>
                <div className="mt-6 grid grid-cols-2 gap-0 border-l border-t border-border">
                  <div className="p-6 border-r border-b border-border">
                    <div className="font-serif-display text-4xl">{stats.text_generations}</div>
                    <div className="overline text-muted-foreground mt-2">Text drafts</div>
                  </div>
                  <div className="p-6 border-b border-border">
                    <div className="font-serif-display text-4xl">{stats.image_generations}</div>
                    <div className="overline text-muted-foreground mt-2">Images</div>
                  </div>
                </div>
              </div>
            </section>

            <section className="border-b border-border p-8">
              <div className="overline text-muted-foreground mb-4">Users · Full control</div>
              <div className="overflow-auto border border-border">
                <table className="w-full text-sm font-mono">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Role</th>
                      <th className="text-left p-3">Plan</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Text</th>
                      <th className="text-right p-3">Img</th>
                      <th className="text-left p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-t border-border" data-testid={`admin-user-row-${u.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {u.email}
                            {u.is_banned && (
                              <span className="text-[10px] font-mono uppercase tracking-widest text-destructive border border-destructive px-1.5 py-0.5">
                                banned
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={u.role === "admin" ? "text-primary" : ""}>{u.role || "user"}</span>
                        </td>
                        <td className="p-3">
                          <Select
                            value={u.plan}
                            onValueChange={(v) => changePlan(u.id, v)}
                          >
                            <SelectTrigger className="rounded-none h-8 w-28 text-xs font-mono" data-testid={`plan-select-${u.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PLAN_OPTIONS.map((p) => (
                                <SelectItem key={p} value={p}>{p}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-3">{u.plan_status}</td>
                        <td className="p-3 text-right">{u.monthly_text_used || 0}</td>
                        <td className="p-3 text-right">{u.monthly_image_used || 0}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-none h-8 px-2"
                              disabled={busyId === u.id}
                              onClick={() => toggleRole(u)}
                              title={u.role === "admin" ? "Demote to user" : "Promote to admin"}
                              data-testid={`role-toggle-${u.id}`}
                            >
                              {u.role === "admin" ? <ShieldOff className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-none h-8 px-2"
                              disabled={busyId === u.id}
                              onClick={() => toggleBan(u)}
                              title={u.is_banned ? "Unban" : "Ban"}
                              data-testid={`ban-toggle-${u.id}`}
                            >
                              {u.is_banned ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-none h-8 px-2 hover:bg-primary hover:text-primary-foreground"
                              disabled={busyId === u.id}
                              onClick={() => adjustCredits(u)}
                              title={`Adjust credits (current: ${u.credits ?? 0})`}
                              data-testid={`adjust-credits-${u.id}`}
                            >
                              <Coins className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-none h-8 px-2 hover:bg-destructive hover:text-destructive-foreground"
                              disabled={busyId === u.id}
                              onClick={() => deleteUser(u)}
                              title="Delete permanently"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="border-b border-border p-8">
              <div className="overline text-muted-foreground mb-4 flex items-center justify-between">
                <span>Latest generations · {gens.length}</span>
                <button
                  onClick={() => load()}
                  className="overline text-[10px] text-primary hover-invert"
                  data-testid="admin-refresh-gens"
                >
                  Refresh
                </button>
              </div>
              <div className="border border-border divide-y divide-border">
                {gens.slice(0, 40).map((g) => {
                  const editing = editingGenId === g.id;
                  return (
                    <div key={g.id} className="p-3 text-sm font-mono" data-testid={`admin-gen-row-${g.id}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-primary uppercase w-16 flex-shrink-0">{g.kind}</span>
                        <span className="text-muted-foreground uppercase text-[10px] w-24 flex-shrink-0 truncate" title={g.tool}>{g.tool || "—"}</span>
                        <span className={`flex-1 min-w-0 truncate ${g.hidden ? "line-through text-muted-foreground" : ""}`} title={g.prompt}>{g.prompt}</span>
                        <span className="text-muted-foreground text-[10px] flex-shrink-0">{g.created_at ? new Date(g.created_at).toLocaleString() : ""}</span>
                        <div className="flex-shrink-0 flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-none h-7 px-2"
                            onClick={() => openGenEditor(g)}
                            title="View / edit"
                            data-testid={`admin-gen-edit-${g.id}`}
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-none h-7 px-2"
                            onClick={() => toggleGenHidden(g)}
                            title={g.hidden ? "Unhide" : "Hide from user"}
                            data-testid={`admin-gen-hide-${g.id}`}
                          >
                            {g.hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-none h-7 px-2 hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => deleteGen(g)}
                            title="Delete generation"
                            data-testid={`admin-gen-delete-${g.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                      {editing && (
                        <div className="mt-3 border border-primary/40 p-3 bg-primary/5" data-testid={`admin-gen-editor-${g.id}`}>
                          <label className="overline text-[10px]">Prompt</label>
                          <textarea
                            value={editingPrompt}
                            onChange={(e) => setEditingPrompt(e.target.value)}
                            rows={2}
                            className="w-full mt-1 p-2 border border-border bg-background font-mono text-xs"
                            data-testid={`admin-gen-editor-prompt-${g.id}`}
                          />
                          {g.kind === "text" && (
                            <>
                              <label className="overline text-[10px] mt-3 block">Output</label>
                              <textarea
                                value={editingOutput}
                                onChange={(e) => setEditingOutput(e.target.value)}
                                rows={8}
                                className="w-full mt-1 p-2 border border-border bg-background font-mono text-xs"
                                data-testid={`admin-gen-editor-output-${g.id}`}
                              />
                            </>
                          )}
                          {g.kind !== "text" && (
                            <p className="mt-3 text-[10px] text-muted-foreground">
                              Media outputs (image/video/audio) can be hidden or deleted, but the binary content itself is stored in Object Storage and not editable inline.
                            </p>
                          )}
                          <div className="mt-3 flex gap-2">
                            <Button
                              size="sm"
                              className="rounded-none h-8 px-3 font-mono text-[10px] uppercase tracking-widest"
                              onClick={() => saveGenEdit(g)}
                              data-testid={`admin-gen-save-${g.id}`}
                            >
                              <Save className="w-3 h-3" /> Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-none h-8 px-3 font-mono text-[10px] uppercase tracking-widest"
                              onClick={() => setEditingGenId(null)}
                              data-testid={`admin-gen-cancel-${g.id}`}
                            >
                              <XIcon className="w-3 h-3" /> Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="border-b border-border p-8">
              <div className="overline text-muted-foreground mb-4">Publish blog post (SEO)</div>
              <form onSubmit={submitBlog} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-blog-form">
                <input required placeholder="Title" value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} className="border border-border p-3 bg-background font-mono text-sm" data-testid="blog-title" />
                <input placeholder="slug-optional" value={blogForm.slug} onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value })} className="border border-border p-3 bg-background font-mono text-sm" />
                <input required placeholder="Excerpt (155 chars)" value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} className="md:col-span-2 border border-border p-3 bg-background font-mono text-sm" />
                <textarea required placeholder="Markdown body" value={blogForm.body} onChange={(e) => setBlogForm({ ...blogForm, body: e.target.value })} rows={6} className="md:col-span-2 border border-border p-3 bg-background font-mono text-sm" />
                <input placeholder="tags, comma, separated" value={blogForm.tags} onChange={(e) => setBlogForm({ ...blogForm, tags: e.target.value })} className="md:col-span-2 border border-border p-3 bg-background font-mono text-sm" />
                <div>
                  <Button type="submit" className="rounded-none h-11 px-6 font-mono uppercase tracking-widest text-xs" data-testid="publish-blog-btn">
                    Publish
                  </Button>
                </div>
              </form>
            </section>

            <HomepageSubmissionsPanel />
          </>
        )}
      </main>
    </div>
  );
}

/* -------------------------- Homepage Submissions Panel -------------------------- */
function HomepageSubmissionsPanel() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/homepage-submissions?status=${tab}`);
      setItems(r.data?.items || []);
    } catch {
      toast.error("Couldn't load submissions.");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  async function decide(sub, action) {
    setBusyId(sub.id);
    try {
      await api.post(`/admin/homepage-submissions/${sub.id}/${action}`, {});
      toast.success(action === "approve" ? "Approved — live on homepage." : "Rejected.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="border-b border-border p-8" data-testid="admin-homepage-submissions">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="overline text-muted-foreground">Homepage submissions</div>
        <div className="flex gap-1 border border-border">
          {["pending", "approved", "rejected"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 overline text-[10px] ${tab === t ? "bg-foreground text-background" : ""}`}
              data-testid={`admin-hp-tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
          Nothing in this queue.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((s) => (
            <div key={s.id} className="border border-border p-4" data-testid={`admin-hp-sub-${s.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="font-serif-display text-lg leading-tight">{s.title}</div>
                <span className="overline text-[10px] px-2 py-0.5 border border-border">{s.kind}</span>
              </div>
              {s.description && <p className="text-xs text-muted-foreground line-clamp-3">{s.description}</p>}
              <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                {s.user?.username ? `@${s.user.username}` : s.user?.name} · {s.user?.email}
              </div>
              <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                Submitted: {s.submitted_at?.slice(0, 16).replace("T", " ")}
              </div>
              {tab === "pending" && (
                <div className="mt-3 flex gap-2">
                  <Button
                    onClick={() => decide(s, "approve")}
                    disabled={busyId === s.id}
                    size="sm"
                    className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid={`admin-hp-approve-${s.id}`}
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                  </Button>
                  <Button
                    onClick={() => decide(s, "reject")}
                    disabled={busyId === s.id}
                    size="sm"
                    variant="outline"
                    className="rounded-none"
                    data-testid={`admin-hp-reject-${s.id}`}
                  >
                    <Ban className="w-3 h-3 mr-1" /> Reject
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
